import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  Shield,
  Phone,
  Instagram,
  RotateCcw,
  Package,
  ListOrdered,
  CheckCircle2,
  ChefHat,
  Check,
  X,
  Trash2,
  Clock,
  Pause,
  Play,
  SlidersHorizontal,
  Plus,
} from "lucide-react";
import { LOGO_URL } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(null); // 'drop' | 'orders' | null
  const [tab, setTab] = useState("orders"); // 'orders' | 'paid' | 'drop' | 'limits'
  const [confirmDelete, setConfirmDelete] = useState(null); // order id
  const [config, setConfig] = useState(null); // { packs: [...], slots: [...] }
  const [configDirty, setConfigDirty] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("gullway_admin_pw");
    if (stored) {
      setPassword(stored);
      verify(stored, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (pw, silent = false) => {
    const trimmed = (pw || "").trim();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": trimmed } });
      setAuthed(true);
      sessionStorage.setItem("gullway_admin_pw", trimmed);
      setPassword(trimmed);
      await loadAll(trimmed);
      if (!silent) toast.success("Welcome back");
    } catch (e) {
      sessionStorage.removeItem("gullway_admin_pw");
      setAuthed(false);
      if (!silent) toast.error("Wrong password");
    } finally {
      setLoading(false);
    }
  };

  const loadAll = async (pw = password) => {
    const headers = { "X-Admin-Password": pw };
    try {
      const [o, s, c] = await Promise.all([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/admin/drop-summary`, { headers }),
        axios.get(`${API}/admin/config`, { headers }),
      ]);
      setOrders(o.data);
      setSummary(s.data);
      setConfig(c.data);
      setConfigDirty(false);
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    await loadAll();
    setLoading(false);
  };

  const togglePaid = async (id, paid) => {
    try {
      await axios.patch(
        `${API}/orders/${id}/paid`,
        { paid },
        { headers: { "X-Admin-Password": password } }
      );
      toast.success(paid ? "Marked paid" : "Marked unpaid");
      await loadAll();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const deleteOrder = async (id) => {
    try {
      await axios.delete(`${API}/orders/${id}`, { headers: { "X-Admin-Password": password } });
      toast.success("Order deleted");
      setConfirmDelete(null);
      await loadAll();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const resetDrop = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/admin/reset-drop`, {}, { headers: { "X-Admin-Password": password } });
      toast.success("New drop started — stock reset.");
      setConfirmReset(null);
      await loadAll();
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const resetOrders = async () => {
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/admin/reset-orders`, {}, { headers: { "X-Admin-Password": password } });
      toast.success(`Cleared ${data.deleted} order${data.deleted === 1 ? "" : "s"}.`);
      setConfirmReset(null);
      await loadAll();
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const togglePause = async () => {
    const next = !(summary?.paused || false);
    try {
      await axios.post(
        `${API}/admin/pause`,
        { paused: next },
        { headers: { "X-Admin-Password": password } }
      );
      toast.success(next ? "Orders paused" : "Orders open again");
      await loadAll();
    } catch (e) {
      toast.error("Couldn't update");
    }
  };

  const mutateConfig = (fn) => {
    setConfig((c) => fn(structuredClone(c)));
    setConfigDirty(true);
  };

  const saveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    try {
      await axios.put(
        `${API}/admin/config`,
        {
          pack_limits: Object.fromEntries(config.packs.map((p) => [p.id, parseInt(p.limit, 10) || 0])),
          slots: config.slots.map((s) => ({ id: s.id, limit: parseInt(s.limit, 10) || 0 })),
        },
        { headers: { "X-Admin-Password": password } }
      );
      toast.success("Limits saved — live on the site now");
      setConfigDirty(false);
      await loadAll();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingConfig(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0D26] flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-[#171D4F]/70 backdrop-blur-xl border border-[#F6F0E8]/10 rounded-3xl p-7">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-[#F4A623]/60" />
            <div>
              <div className="font-display text-2xl uppercase text-[#F6F0E8]">Gullway Admin</div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#F4A623]">Crew Access</div>
            </div>
          </div>
          <label className="mt-6 block text-xs uppercase tracking-[0.25em] text-[#F4A623]">Admin Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verify(password)}
            placeholder="••••••••"
            data-testid="admin-password-input"
            className="mt-1.5 w-full bg-[#0A0D26] border border-[#F6F0E8]/15 text-[#F6F0E8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#ED0B09] focus:ring-1 focus:ring-[#ED0B09]"
          />
          <button
            type="button"
            onClick={() => verify(password)}
            disabled={loading || !password}
            data-testid="admin-login-button"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#ED0B09] hover:bg-[#B42A27] disabled:opacity-60 text-[#F6F0E8] font-bold uppercase tracking-[0.18em] py-3 rounded-full transition-all shadow-[0_0_24px_rgba(237,11,9,0.55)]"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Shield size={16} />}
            Enter
          </button>
        </div>
      </div>
    );
  }

  const unpaidOrders = orders.filter((o) => !o.paid);
  const paidOrders = orders.filter((o) => o.paid);

  return (
    <div className="min-h-screen bg-[#0A0D26] text-[#F6F0E8] pb-20">
      <header className="border-b border-[#F6F0E8]/10 bg-[#0A0D26]/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="" className="h-8 w-8 sm:h-9 sm:w-9 rounded-full object-cover ring-2 ring-[#F4A623]/60" />
            <div className="font-display text-lg sm:text-xl uppercase">
              Gullway <span className="text-[#ED0B09]">Admin</span>
            </div>
          </div>
          {/* Desktop-only header actions */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={refreshAll}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 bg-[#171D4F] hover:bg-[#171D4F]/70 border border-[#F6F0E8]/10 text-[#F6F0E8] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              type="button"
              onClick={togglePause}
              data-testid="admin-pause-button"
              className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full border ${
                summary?.paused
                  ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/25"
                  : "bg-[#F4A623]/15 border-[#F4A623]/40 text-[#F4A623] hover:bg-[#F4A623]/25"
              }`}
            >
              {summary?.paused ? <Play size={13} /> : <Pause size={13} />}
              {summary?.paused ? "Resume Orders" : "Pause Orders"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmReset("orders")}
              data-testid="admin-reset-orders-button"
              className="inline-flex items-center gap-2 bg-[#ED0B09]/15 hover:bg-[#ED0B09]/25 border border-[#ED0B09]/40 text-[#ED0B09] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full"
            >
              <Trash2 size={13} /> Reset Orders
            </button>
            <button
              type="button"
              onClick={() => setConfirmReset("drop")}
              data-testid="admin-reset-drop-button"
              className="inline-flex items-center gap-2 bg-[#F4A623]/15 hover:bg-[#F4A623]/25 border border-[#F4A623]/40 text-[#F4A623] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full"
            >
              <RotateCcw size={13} /> Reset Drop
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-5">
        {/* Mobile-only quick actions row */}
        <div className="sm:hidden grid grid-cols-2 gap-2 mb-2">
          <button
            type="button"
            onClick={refreshAll}
            data-testid="admin-refresh-button-mobile"
            className="inline-flex items-center justify-center gap-1.5 bg-[#171D4F] border border-[#F6F0E8]/10 text-[#F6F0E8] text-[10px] font-bold uppercase tracking-widest py-2.5 rounded-full"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            type="button"
            onClick={togglePause}
            data-testid="admin-pause-button-mobile"
            className={`inline-flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest py-2.5 rounded-full border ${
              summary?.paused
                ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300"
                : "bg-[#F4A623]/15 border-[#F4A623]/40 text-[#F4A623]"
            }`}
          >
            {summary?.paused ? <Play size={12} /> : <Pause size={12} />}
            {summary?.paused ? "Resume" : "Pause Orders"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmReset("orders")}
            data-testid="admin-reset-orders-button-mobile"
            className="inline-flex items-center justify-center gap-1.5 bg-[#ED0B09]/15 border border-[#ED0B09]/40 text-[#ED0B09] text-[10px] font-bold uppercase tracking-widest py-2.5 rounded-full"
          >
            <Trash2 size={12} /> Reset Orders
          </button>
          <button
            type="button"
            onClick={() => setConfirmReset("drop")}
            data-testid="admin-reset-drop-button-mobile"
            className="inline-flex items-center justify-center gap-1.5 bg-[#F4A623]/15 border border-[#F4A623]/40 text-[#F4A623] text-[10px] font-bold uppercase tracking-widest py-2.5 rounded-full"
          >
            <RotateCcw size={12} /> Reset Drop
          </button>
        </div>

        {/* Paused banner */}
        {summary?.paused && (
          <div
            data-testid="admin-paused-banner"
            className="mb-3 px-4 py-2.5 rounded-2xl bg-[#F4A623]/10 border border-[#F4A623]/40 text-[#F4A623] text-xs uppercase tracking-widest flex items-center gap-2"
          >
            <Pause size={13} /> Orders are paused — site shows "Sold Out"
          </div>
        )}

        {/* Reset confirm */}
        {confirmReset && (
          <div
            className={`mb-4 p-4 rounded-2xl border ${
              confirmReset === "drop"
                ? "border-[#F4A623]/50 bg-[#F4A623]/10"
                : "border-[#ED0B09]/50 bg-[#ED0B09]/10"
            }`}
            data-testid="admin-reset-confirm"
          >
            <div className="font-display text-lg sm:text-xl uppercase text-[#F6F0E8]">
              {confirmReset === "drop" ? "Reset drop for next round?" : "Clear all orders?"}
            </div>
            <p className="mt-1 text-xs sm:text-sm text-[#F6F0E8]/75">
              {confirmReset === "drop"
                ? "Archives this drop and starts fresh stock for all packs. Past orders stay visible."
                : "Permanently deletes every order in the dashboard. Use this to clear out the list before a new drop."}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={confirmReset === "drop" ? resetDrop : resetOrders}
                disabled={resetting}
                data-testid="admin-reset-confirm-yes"
                className={`flex-1 inline-flex items-center justify-center gap-1.5 font-bold uppercase tracking-widest text-xs px-3 py-2.5 rounded-full disabled:opacity-60 ${
                  confirmReset === "drop"
                    ? "bg-[#F4A623] hover:bg-[#F4A623]/85 text-[#171D4F]"
                    : "bg-[#ED0B09] hover:bg-[#B42A27] text-[#F6F0E8]"
                }`}
              >
                {resetting ? <Loader2 className="animate-spin" size={12} /> : confirmReset === "drop" ? <RotateCcw size={12} /> : <Trash2 size={12} />}
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setConfirmReset(null)}
                data-testid="admin-reset-confirm-no"
                className="flex-1 inline-flex items-center justify-center bg-transparent border border-[#F6F0E8]/15 text-[#F6F0E8] font-bold uppercase tracking-widest text-xs px-3 py-2.5 rounded-full"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Tracker — how much of each pack ordered this drop */}
        {summary && (
          <div
            className="mb-4 bg-[#171D4F]/60 border border-[#F6F0E8]/10 rounded-2xl p-3"
            data-testid="admin-mini-tracker"
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F4A623] flex items-center gap-1.5">
                <Package size={11} /> This Drop
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#F6F0E8]/55">
                {summary.total_orders} order{summary.total_orders === 1 ? "" : "s"}
              </div>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {summary.items.map((i) => {
                const pct = Math.min(100, Math.round((i.total / i.limit) * 100));
                const full = i.total >= i.limit;
                return (
                  <div
                    key={i.id}
                    data-testid={`admin-tracker-${i.id}`}
                    className={`bg-[#0A0D26] rounded-xl px-2 py-2 text-center border ${
                      full ? "border-[#ED0B09]/50" : "border-[#F6F0E8]/10"
                    }`}
                  >
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-[#F6F0E8]/55">
                      {i.size}pk
                    </div>
                    <div className={`font-display text-base sm:text-lg leading-none mt-0.5 ${full ? "text-[#ED0B09]" : "text-[#F6F0E8]"}`}>
                      {i.total}
                      <span className="text-[#F6F0E8]/40 text-xs">/{i.limit}</span>
                    </div>
                    <div className="mt-1.5 h-1 rounded-full bg-[#171D4F] overflow-hidden">
                      <div
                        className={`h-full ${full ? "bg-[#ED0B09]" : "bg-[#F4A623]"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab bar */}
        <div className="grid grid-cols-4 gap-1 p-1 bg-[#171D4F]/60 border border-[#F6F0E8]/10 rounded-2xl mb-5" role="tablist" data-testid="admin-tabs">
          <Tab id="orders" current={tab} setTab={setTab} icon={ListOrdered} label="Orders" count={unpaidOrders.length} />
          <Tab id="paid" current={tab} setTab={setTab} icon={CheckCircle2} label="Paid" count={paidOrders.length} />
          <Tab id="drop" current={tab} setTab={setTab} icon={ChefHat} label="Drop" />
          <Tab id="limits" current={tab} setTab={setTab} icon={SlidersHorizontal} label="Limits" />
        </div>

        {tab === "orders" && (
          <OrdersList
            orders={unpaidOrders}
            loading={loading}
            emptyText="No new pre-orders yet."
            onTogglePaid={togglePaid}
            onDelete={(id) => setConfirmDelete(id)}
          />
        )}

        {tab === "paid" && (
          <OrdersList
            orders={paidOrders}
            loading={loading}
            emptyText="No paid orders yet."
            onTogglePaid={togglePaid}
            onDelete={(id) => setConfirmDelete(id)}
            paidView
          />
        )}

        {tab === "drop" && <DropSummary summary={summary} />}

        {tab === "limits" && (
          <LimitsPanel
            config={config}
            onMutate={mutateConfig}
            onSave={saveConfig}
            saving={savingConfig}
            dirty={configDirty}
          />
        )}
      </main>

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-[#0A0D26]/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="admin-delete-modal"
        >
          <div className="w-full sm:max-w-sm bg-[#171D4F] border border-[#ED0B09]/40 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-[#ED0B09]">
              <Trash2 size={18} />
              <div className="font-display text-xl uppercase">Delete order?</div>
            </div>
            <p className="mt-2 text-sm text-[#F6F0E8]/75">
              This permanently removes order <span className="font-mono text-[#F4A623]">#{confirmDelete.slice(0, 8).toUpperCase()}</span>.
              Stock for this drop will go back up.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => deleteOrder(confirmDelete)}
                data-testid="admin-delete-confirm"
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#ED0B09] hover:bg-[#B42A27] text-[#F6F0E8] font-bold uppercase tracking-widest text-xs px-3 py-2.5 rounded-full"
              >
                <Trash2 size={12} /> Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="flex-1 inline-flex items-center justify-center bg-transparent border border-[#F6F0E8]/15 text-[#F6F0E8] font-bold uppercase tracking-widest text-xs px-3 py-2.5 rounded-full"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tab({ id, current, setTab, icon: Icon, label, count }) {
  const active = current === id;
  return (
    <button
      type="button"
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      role="tab"
      aria-selected={active}
      className={`relative inline-flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl text-[11px] sm:text-xs font-bold uppercase tracking-widest transition-all ${
        active
          ? "bg-[#ED0B09] text-[#F6F0E8] shadow-[0_0_16px_rgba(237,11,9,0.45)]"
          : "text-[#F6F0E8]/70 hover:text-[#F6F0E8]"
      }`}
    >
      <Icon size={13} />
      {label}
      {count !== undefined && count > 0 && (
        <span
          className={`ml-1 text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center ${
            active ? "bg-[#F6F0E8] text-[#171D4F]" : "bg-[#F4A623]/15 text-[#F4A623]"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function OrdersList({ orders, loading, emptyText, onTogglePaid, onDelete, paidView = false }) {
  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-[#F6F0E8]/70">
        <Loader2 className="animate-spin mr-2" /> Loading...
      </div>
    );
  }
  if (orders.length === 0) {
    return (
      <div className="text-center py-16 bg-[#171D4F]/40 border border-[#F6F0E8]/10 rounded-2xl">
        <p className="text-[#F6F0E8]/65 text-sm">{emptyText}</p>
      </div>
    );
  }

  // Group by pickup_time (handles any time — slots are admin-editable)
  const fmtTime = (k) => {
    if (k === "__none") return "No time selected";
    if (!/^\d{2}:\d{2}$/.test(k)) return k;
    const h = parseInt(k.slice(0, 2), 10);
    return `${h % 12 || 12}:${k.slice(3)} ${h < 12 ? "AM" : "PM"}`;
  };
  const grouped = {};
  orders.forEach((o) => {
    const k = o.customer?.pickup_time || "__none";
    (grouped[k] = grouped[k] || []).push(o);
  });
  const slotKeys = Object.keys(grouped).sort(
    (a, b) => (a === "__none") - (b === "__none") || a.localeCompare(b)
  );

  return (
    <div className="space-y-6" data-testid="admin-orders-list">
      {slotKeys.map((k) => (
        <section key={k} data-testid={`admin-orders-group-${k}`}>
          <div className="flex items-baseline justify-between mb-3 px-1">
            <h3 className="font-display text-xl sm:text-2xl uppercase text-[#F6F0E8] flex items-center gap-2">
              <Clock size={14} className="text-[#F4A623]" />
              {fmtTime(k)}
            </h3>
            <span className="text-[10px] uppercase tracking-widest text-[#F6F0E8]/55">
              {grouped[k].length} order{grouped[k].length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {grouped[k].map((o) => (
              <OrderCard key={o.id} order={o} onTogglePaid={onTogglePaid} onDelete={onDelete} paidView={paidView} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function OrderCard({ order: o, onTogglePaid, onDelete }) {
  const ref = `#${o.id.slice(0, 8).toUpperCase()}`;
  const item = o.items[0] || {};
  const when = new Date(o.created_at);
  const igHandle = o.customer.instagram ? o.customer.instagram.replace(/^@+/, "") : null;

  return (
    <article
      data-testid={`admin-order-${o.id}`}
      className={`relative bg-[#171D4F]/60 border rounded-2xl p-4 flex flex-col gap-3 ${
        o.paid ? "border-emerald-500/40" : "border-[#F6F0E8]/10"
      }`}
    >
      {o.paid && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 px-2 py-1 rounded-full">
          <Check size={11} /> Paid
        </div>
      )}

      {/* Header */}
      <div>
        <div className="font-display text-2xl uppercase leading-tight text-[#F6F0E8] pr-16 truncate">
          {o.customer.name}
        </div>
        {igHandle && (
          <div className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-[#F4A623]" data-testid={`admin-order-instagram-${o.id}`}>
            <Instagram size={12} /> @{igHandle}
          </div>
        )}
        <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#F6F0E8]/55">
          <span>{ref}</span>
          <span className="text-[#F6F0E8]/30">·</span>
          <span>{when.toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
        </div>
      </div>

      {/* Pack */}
      <div className="bg-[#0A0D26] border border-[#F6F0E8]/10 rounded-xl p-3 flex items-center justify-between">
        <div>
          <div className="font-display text-lg uppercase text-[#F6F0E8] leading-none">{item.pack_name}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#F6F0E8]/55 mt-1">
            {item.pack_size} nibs · qty {item.quantity}
          </div>
        </div>
        <div className="font-display text-2xl text-[#F4A623] leading-none">${o.subtotal.toFixed(2)}</div>
      </div>

      {/* Contact */}
      <a
        href={`tel:${o.customer.phone}`}
        className="inline-flex items-center gap-1.5 text-sm text-[#F6F0E8]/85 hover:text-[#F4A623] -mt-1"
      >
        <Phone size={13} className="text-[#F4A623]" /> {o.customer.phone}
      </a>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-auto pt-1">
        <button
          type="button"
          onClick={() => onTogglePaid(o.id, !o.paid)}
          data-testid={`admin-order-toggle-paid-${o.id}`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2.5 rounded-full transition-all ${
            o.paid
              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25"
              : "bg-emerald-500 text-[#0A0D26] hover:bg-emerald-400 shadow-[0_0_18px_rgba(16,185,129,0.4)]"
          }`}
        >
          {o.paid ? <X size={12} /> : <Check size={12} />}
          {o.paid ? "Mark Unpaid" : "Mark Paid"}
        </button>
        <button
          type="button"
          onClick={() => onDelete(o.id)}
          data-testid={`admin-order-delete-${o.id}`}
          className="inline-flex items-center justify-center bg-transparent border border-[#ED0B09]/40 text-[#ED0B09] hover:bg-[#ED0B09]/15 text-xs font-bold px-3 py-2.5 rounded-full"
          aria-label="Delete order"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </article>
  );
}

function DropSummary({ summary }) {
  if (!summary) {
    return (
      <div className="flex items-center justify-center py-16 text-[#F6F0E8]/70">
        <Loader2 className="animate-spin mr-2" /> Loading...
      </div>
    );
  }
  const totalNibs = summary.items.reduce((s, i) => s + i.nibs_to_cook, 0);
  const totalPacks = summary.items.reduce((s, i) => s + i.total, 0);
  return (
    <div className="space-y-4" data-testid="admin-drop-summary">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Orders" value={summary.total_orders} />
        <Stat label="Paid" value={summary.paid_orders} accent="text-emerald-300" />
        <Stat label="Unpaid" value={summary.unpaid_orders} accent="text-[#F4A623]" />
      </div>

      <div className="bg-[#171D4F]/60 border border-[#F6F0E8]/10 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F4A623] flex items-center gap-1.5">
            <Package size={12} /> Packs to make
          </div>
          <div className="text-xs uppercase tracking-widest text-[#F6F0E8]/55">{totalPacks} packs · {totalNibs} nibs</div>
        </div>
        <ul className="space-y-2">
          {summary.items.map((i) => (
            <li
              key={i.id}
              data-testid={`admin-drop-pack-${i.id}`}
              className="flex items-center justify-between gap-3 bg-[#0A0D26] border border-[#F6F0E8]/10 rounded-xl px-3 py-2.5"
            >
              <div>
                <div className="font-display text-base uppercase text-[#F6F0E8] leading-none">{i.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#F6F0E8]/55 mt-1 flex items-center gap-2">
                  <span className="text-emerald-300">{i.paid} paid</span>
                  <span className="text-[#F6F0E8]/30">·</span>
                  <span className="text-[#F4A623]">{i.unpaid} unpaid</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-[#F4A623] leading-none">{i.total}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#F6F0E8]/55 mt-1">
                  {i.nibs_to_cook} nibs
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-[#F6F0E8]" }) {
  return (
    <div className="bg-[#171D4F]/60 border border-[#F6F0E8]/10 rounded-2xl p-3 text-center">
      <div className={`font-display text-3xl leading-none ${accent}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#F6F0E8]/55 mt-1.5">{label}</div>
    </div>
  );
}

/* ---------- Editable limits (packs + pickup slots) ---------- */

const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 6; h <= 18; h++) {
    for (const m of [0, 30]) {
      if (h === 18 && m > 0) break;
      out.push(`${String(h).padStart(2, "0")}:${m === 0 ? "00" : "30"}`);
    }
  }
  return out;
})();

function fmtSlotTime(id) {
  const h = parseInt(id.slice(0, 2), 10);
  return `${h % 12 || 12}:${id.slice(3)} ${h < 12 ? "AM" : "PM"}`;
}

function LimitsPanel({ config, onMutate, onSave, saving, dirty }) {
  if (!config) {
    return (
      <div className="flex items-center justify-center py-16 text-[#F6F0E8]/70">
        <Loader2 className="animate-spin mr-2" /> Loading...
      </div>
    );
  }

  const usedTimes = config.slots.map((s) => s.id);
  const allTimesUsed = TIME_OPTIONS.every((t) => usedTimes.includes(t));

  const setPackLimit = (pid, value) =>
    onMutate((c) => {
      c.packs = c.packs.map((p) => (p.id === pid ? { ...p, limit: value } : p));
      return c;
    });

  const setSlotTime = (idx, value) =>
    onMutate((c) => {
      c.slots = c.slots
        .map((s, i) => (i === idx ? { ...s, id: value, label: fmtSlotTime(value) } : s))
        .sort((a, b) => a.id.localeCompare(b.id));
      return c;
    });

  const setSlotLimit = (idx, value) =>
    onMutate((c) => {
      c.slots = c.slots.map((s, i) => (i === idx ? { ...s, limit: value } : s));
      return c;
    });

  const removeSlot = (idx) =>
    onMutate((c) => {
      c.slots = c.slots.filter((_, i) => i !== idx);
      return c;
    });

  const addSlot = () => {
    const free = TIME_OPTIONS.find((t) => !usedTimes.includes(t));
    if (!free) {
      toast.error("All times between 6 AM and 6 PM are already used");
      return;
    }
    onMutate((c) => {
      c.slots = [...c.slots, { id: free, label: fmtSlotTime(free), limit: 20 }].sort((a, b) =>
        a.id.localeCompare(b.id)
      );
      return c;
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-limits-panel">
      {/* Pack limits */}
      <div className="bg-[#171D4F]/60 border border-[#F6F0E8]/10 rounded-2xl p-4">
        <div className="text-xs uppercase tracking-[0.25em] text-[#F4A623] flex items-center gap-1.5 mb-3">
          <Package size={12} /> Packs available per drop
        </div>
        <ul className="space-y-2">
          {config.packs.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 bg-[#0A0D26] border border-[#F6F0E8]/10 rounded-xl px-3 py-2.5"
            >
              <div>
                <div className="font-display text-base uppercase text-[#F6F0E8] leading-none">{p.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#F6F0E8]/55 mt-1">
                  {p.size} nibs · ${p.price}
                </div>
              </div>
              <input
                type="number"
                min={0}
                max={50}
                inputMode="numeric"
                value={p.limit}
                onChange={(e) => setPackLimit(p.id, e.target.value)}
                data-testid={`admin-limit-pack-${p.id}`}
                aria-label={`${p.name} limit`}
                className="w-20 bg-[#171D4F] border border-[#F6F0E8]/15 rounded-lg px-2 py-1.5 text-center font-display text-xl text-[#F4A623] focus:outline-none focus:border-[#ED0B09]"
              />
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[10px] uppercase tracking-widest text-[#F6F0E8]/45">0–50 per pack</p>
      </div>

      {/* Pickup slots */}
      <div className="bg-[#171D4F]/60 border border-[#F6F0E8]/10 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F4A623] flex items-center gap-1.5">
            <Clock size={12} /> Pickup time slots
          </div>
          <button
            type="button"
            onClick={addSlot}
            disabled={allTimesUsed}
            data-testid="admin-add-slot"
            className="inline-flex items-center gap-1 bg-[#F4A623]/15 border border-[#F4A623]/40 text-[#F4A623] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full disabled:opacity-40"
          >
            <Plus size={11} /> Add slot
          </button>
        </div>
        <ul className="space-y-2">
          {config.slots.map((s, idx) => (
            <li
              key={`${s.id}-${idx}`}
              data-testid={`admin-slot-row-${idx}`}
              className="flex items-center gap-2 bg-[#0A0D26] border border-[#F6F0E8]/10 rounded-xl px-3 py-2.5"
            >
              <select
                value={s.id}
                onChange={(e) => setSlotTime(idx, e.target.value)}
                data-testid={`admin-slot-time-${idx}`}
                aria-label={`Slot ${idx + 1} time`}
                className="flex-1 bg-[#171D4F] border border-[#F6F0E8]/15 rounded-lg px-2 py-1.5 text-sm text-[#F6F0E8] focus:outline-none focus:border-[#ED0B09]"
              >
                {TIME_OPTIONS.filter((t) => t === s.id || !usedTimes.includes(t)).map((t) => (
                  <option key={t} value={t}>
                    {fmtSlotTime(t)}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={0}
                max={50}
                inputMode="numeric"
                value={s.limit}
                onChange={(e) => setSlotLimit(idx, e.target.value)}
                data-testid={`admin-limit-slot-${idx}`}
                aria-label={`Slot ${idx + 1} limit`}
                className="w-20 bg-[#171D4F] border border-[#F6F0E8]/15 rounded-lg px-2 py-1.5 text-center font-display text-xl text-[#F4A623] focus:outline-none focus:border-[#ED0B09]"
              />
              <button
                type="button"
                onClick={() => removeSlot(idx)}
                disabled={config.slots.length <= 1}
                data-testid={`admin-slot-remove-${idx}`}
                aria-label={`Remove slot ${idx + 1}`}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-transparent border border-[#ED0B09]/40 text-[#ED0B09] hover:bg-[#ED0B09]/15 disabled:opacity-30"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[10px] uppercase tracking-widest text-[#F6F0E8]/45">
          Times between 6:00 AM–6:00 PM · 0–50 orders per slot
        </p>
      </div>

      {/* Save */}
      <button
        type="button"
        onClick={onSave}
        disabled={!dirty || saving}
        data-testid="admin-save-limits"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#ED0B09] hover:bg-[#B42A27] disabled:opacity-50 text-[#F6F0E8] font-bold uppercase tracking-[0.18em] py-3.5 rounded-full transition-all shadow-[0_0_24px_rgba(237,11,9,0.45)]"
      >
        {saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
        {saving ? "Saving..." : dirty ? "Save Limits" : "Saved"}
      </button>
      <p className="text-center text-[10px] uppercase tracking-widest text-[#F6F0E8]/45 pb-2">
        Saving updates this site instantly — no redeploy needed
      </p>
    </div>
  );
}
